import React, { useState } from 'react';
import { extractYouTubeVideoId, getYouTubeEmbedUrlWithTimestamp, getYouTubeThumbnailUrl } from '../../utils/youtubeUtils';

interface YouTubeLinkPreviewProps {
  url: string;
  className?: string;
}

const YouTubeLinkPreview: React.FC<YouTubeLinkPreviewProps> = ({ url, className = '' }) => {
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(false);
  
  const videoId = extractYouTubeVideoId(url);
  const embedUrl = getYouTubeEmbedUrlWithTimestamp(url);
  const thumbnailUrl = getYouTubeThumbnailUrl(url, 'medium');
  
  if (!videoId || !embedUrl) {
    return null;
  }

  const handlePlayClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsPlayerOpen(true);
  };

  const handleClosePlayer = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsPlayerOpen(false);
  };

  return (
    <div className={`youtube-preview ${className}`}>
      {!isPlayerOpen ? (
        // Miniature avec bouton play
        <div 
          className="relative rounded-xl overflow-hidden bg-black group cursor-pointer border border-emerald-800/30"
          onClick={handlePlayClick}
        >
          {/* Miniature */}
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            {!thumbnailError && thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt="Miniature YouTube"
                className="absolute inset-0 w-full h-full object-cover"
                onError={() => setThumbnailError(true)}
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-red-900/30 to-gray-900/30 flex items-center justify-center">
                <svg className="w-16 h-16 text-red-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </div>
            )}
            
            {/* Overlay avec bouton play */}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 flex items-center justify-center transition-all duration-200">
              <div className="w-16 h-16 bg-red-600/90 rounded-full flex items-center justify-center backdrop-blur-sm transform group-hover:scale-110 transition-transform duration-200 shadow-lg">
                <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </div>
            </div>
          </div>
          
          {/* Informations de la vidéo */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-red-400 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              <span className="text-white text-sm font-medium">Vidéo YouTube</span>
            </div>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-300 hover:text-white text-xs truncate block mt-1 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              {url}
            </a>
          </div>
        </div>
      ) : (
        // Lecteur intégré
        <div className="relative rounded-xl overflow-hidden bg-black border border-emerald-800/30">
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              src={embedUrl}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
            />
          </div>
          
          {/* Bouton de fermeture */}
          <button
            onClick={handleClosePlayer}
            className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 rounded-full p-2 transition-colors backdrop-blur-sm"
            title="Fermer le lecteur"
          >
            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {/* Lien vers YouTube */}
          <div className="absolute bottom-2 left-2">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black/60 hover:bg-black/80 rounded-full p-2 transition-colors backdrop-blur-sm"
              title="Ouvrir sur YouTube"
              onClick={(e) => e.stopPropagation()}
            >
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default YouTubeLinkPreview;
